import { ViewportScroller } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { ConfirmationService, FilterService, MenuItem, MessageService } from 'primeng/api';
import { Calendar } from 'primeng/calendar';
import { AuthGateway } from 'src/app/application/gateway/AuthGateway';
import { InvitationCodeGateway } from 'src/app/application/gateway/InvitationCodeGateway';
import { InvitationCode, InvitationCodeCreate, InvitationCodeInactive } from 'src/app/domain/models/InvitationCode';
import { Lang } from 'src/app/domain/models/Language';
import { BasicUser } from 'src/app/domain/models/User';
import { ErrorMessageUtil } from 'src/app/ui/util/error-message-util';

Calendar.prototype.getDateFormat = () => 'dd-mm-yy';

@Component({
  selector: 'app-admin-invitation-codes',
  templateUrl: './admin-invitation-codes.component.html',
  styleUrls: ['./admin-invitation-codes.component.css'],
  providers: [ConfirmationService]
})
export class AdminInvitationCodesComponent implements OnInit {

  breadcrumbHome: MenuItem = {}

  requestCodes: InvitationCode[] = []
  invitationCodes: InvitationCode[] = []
  email: string = '';
  fullname: string = '';
  loading: boolean = false
  submit: boolean = false
  profile: BasicUser | undefined
  langs: Lang[] = []
  lang: Lang | undefined

  formGroup: FormGroup = this._formBuilder.group({
    email: ['', [Validators.required, Validators.email]],
    fullname: ['', Validators.required],
    language: ['', Validators.required]
  })

  isSectionLoaded: boolean = false;
  fragmentToScroll: string | null = null;

  constructor(private _router: Router,
    private _formBuilder: FormBuilder,
    private _authService: AuthGateway,
    private _messageService: MessageService,
    private _invitationCodeService: InvitationCodeGateway,
    public translate: TranslateService,
    private route: ActivatedRoute,
    private viewportScroller: ViewportScroller,
    private readonly filterService: FilterService,
  ){
  }

  ngOnInit(): void {
    this.profile = this._authService.getUser()
    if (this.profile) {
      this.translate.use(this.profile.lang)
    }
    this.loadBreadcrumb();
    document.documentElement.style.scrollBehavior = 'smooth';

    this.route.fragment.subscribe(fragment => {
      this.fragmentToScroll = fragment;
    });

    this.langs = [
      {
        label: 'English',
        lang: 'en',
        icon: 'GB.png'
      },
      {
        label: 'Español',
        lang: 'es',
        icon: 'ES.png'
      }
    ]
    this.lang = this.langs.find(x => x.lang == 'en')
    this.loadInvitationCodes()

    this.filterService.register('filter-date', (value: any, filter: any): boolean => {
      if (!filter) return true
      const found = moment(value).format("DD-MM-YYYY") === moment(filter).format("DD-MM-YYYY")
      return found;
    });
  }

  loadBreadcrumb() {
    this.translate.get([
      'InvitationCodePage.PageHomeTitle',
    ]).subscribe({
      next: (tranlations) => {
        this.breadcrumbHome = {
          label: tranlations['InvitationCodePage.PageHomeTitle']
        }
      },
      error: () => {}
    })
  }

  loadInvitationCodes() {
    this._invitationCodeService.getAll().subscribe({
      next: (v) => {
        this.invitationCodes = v.filter(x => x.code !== "")
        this.requestCodes = v.filter(x => x.isActive && x.isRequest && x.code === "")

        // Ejecutar fragment scroll cuando termine el llamado al endpoint
        this.isSectionLoaded = true;
        this.tryScrollToFragment();
      },
      error: (e) => {
        this.isSectionLoaded = true;
        this.tryScrollToFragment();
        this._messageService.add({
          severity: 'error',
          summary: this.translate.instant('InvitationCodePage.FailedActionTitle'),
          detail: this.translate.instant('InvitationCodePage.RetrieveCodesErrorText')
        });
      },
      complete: () =>  {
        this.loading = false
      }
    })
  }

  tryScrollToFragment(): void {
    if (this.fragmentToScroll && this.isSectionLoaded) {
      setTimeout(() => {
        this.viewportScroller.scrollToAnchor(this.fragmentToScroll!);
        this.fragmentToScroll = null;
      }, 100);
    }
  }

  sendGenerationCode(request: InvitationCodeCreate) {

    this.loading = true
    this._invitationCodeService.create(request).subscribe({
      next: (v) => {
        this.loadInvitationCodes()
        this._messageService.add({
          severity: 'success',
          summary: this.translate.instant('InvitationCodePage.SuccessfulActionTitle'),
          detail: this.translate.instant('InvitationCodePage.SuccessGeneratingCodeText')
        })
        this.submit = false
        this.loading = false
        this.formGroup.reset()
      },
      error: (e) => {
        this.submit = false
        let errorMessage = ''
        switch(this.profile?.lang) {
          case 'es':
            errorMessage = e.error.messageEs
            break
          case 'en':
            errorMessage = e.error.messageEn
            break
        }
        if(e.status === 500){
          this._messageService.add({
            severity: 'error',
            summary: this.translate.instant('InvitationCodePage.FailedActionTitle'),
            detail: this.translate.instant('InvitationCodePage.ErrorGeneratingCodeText')
          })
        }
        else {
          this._messageService.add({
            severity: 'error',
            summary: this.translate.instant('InvitationCodePage.FailedActionTitle'),
            detail: errorMessage
          });
        }
        this.loading = false
      }
    })

  }

  createCode(requestCode: InvitationCode) {
    if (requestCode.fullname && requestCode.email) {
      let request: InvitationCodeCreate = {
        email: requestCode.email,
        fullname: requestCode.fullname,
        language: requestCode.language ?? "en",
        isRequest: true
      }

      this.sendGenerationCode(request)
    }
  }

  generateCode() {
    this.submit = true
    if(this.formGroup.valid) {
      let request: InvitationCodeCreate = {
        email: this.email,
        fullname: this.fullname,
        language: this.lang?.lang ?? "en",
        isRequest: false
      }

      this.sendGenerationCode(request)
    }
    else {
      this.loading = false
    }
  }

  inactiveCode(id: number) {
    this.loading = true
    let inactiveCode: InvitationCodeInactive = {
      id: id
    }
    this._invitationCodeService.deactivate(inactiveCode).subscribe({
      next: (v) => {
        this.loadInvitationCodes()
        this._messageService.add({
          severity: 'success',
          summary: this.translate.instant('InvitationCodePage.SuccessfulActionTitle'),
          detail: this.translate.instant('InvitationCodePage.SuccessDeletingCodeText')
        })
        this.loading = false
      },
      error: (e) => {
        this.loading = false
        this._messageService.add({
          severity: 'error',
          summary: this.translate.instant('InvitationCodePage.FailedActionTitle'),
          detail: this.translate.instant('InvitationCodePage.ErrorDeletingCodeText')
        });
      },
      complete: () =>  {
        this.loading = false
      }
    })
  }

  getErrorMessage(fieldName: string): string {
    return ErrorMessageUtil.getErrorMessage(this.formGroup, this.translate, fieldName)
  }

  get placeholder(): string {
    return this.translate.instant('SwitchLangComponent.SwitchLangComponentPlaceholder')
  }

  switchLang = () => {
    this.lang = this.langs.find(x => x.lang == this.formGroup.controls['language'].value)
  }

  showLanguage(language: string): string | undefined {
    return this.langs.find(x => x.lang == language)?.label
  }

  ngOnDestroy(): void {
    document.documentElement.style.scrollBehavior = 'auto';
  }
}
